<script setup lang="ts">
import type { InvoiceIndexEntry } from '@/packages/api/src';
import TableRow from '@/Components/TableRow.vue';
import { router } from '@inertiajs/vue3';
import InvoiceMoreOptionsDropdown from './InvoiceMoreOptionsDropdown.vue';
import InvoiceDownloadDropdown from './InvoiceDownloadDropdown.vue';
import { ArrowDownTrayIcon } from '@heroicons/vue/20/solid';
import LoadingSpinner from '@/packages/ui/src/LoadingSpinner.vue';
import { formatDate, getDayJsInstance, getLocalizedDayJs } from '@/packages/ui/src/utils/time';
import { useQueryClient } from '@tanstack/vue-query';
import { useNotificationsStore } from '@/utils/notification';
import { api } from '@/packages/api/src';
import { getCurrentOrganizationId } from '@/utils/useUser';
import { useMutation } from '@tanstack/vue-query';
import { Button } from '@/packages/ui/src/Buttons';
import { inject, type ComputedRef } from 'vue';
import type { Organization } from '@/packages/api/src';
const { handleApiRequestNotifications } = useNotificationsStore();
const queryClient = useQueryClient();

const props = defineProps<{
    invoice: InvoiceIndexEntry;
}>();

const deleteInvoiceMutation = useMutation({
    mutationFn: async () => {
        const organizationId = getCurrentOrganizationId();
        if (organizationId === null) {
            throw new Error('No current organization id - delete invoice');
        }
        return await api.deleteInvoice(undefined, {
            params: {
                organization: organizationId,
                invoice: props.invoice.id,
            },
        });
    },
    onSuccess: () => {
        queryClient.invalidateQueries({
            queryKey: ['invoices'],
        });
    },
});

const updateInvoicePaidStatusMutation = useMutation({
    mutationFn: async (paidDate: string | null) => {
        const organizationId = getCurrentOrganizationId();
        if (organizationId === null) {
            throw new Error('No current organization id - update invoice');
        }
        return await api.updateInvoice(
            { paid_date: paidDate },
            {
                params: {
                    organization: organizationId,
                    invoice: props.invoice.id,
                },
            }
        );
    },
    onSuccess: () => {
        queryClient.invalidateQueries({
            queryKey: ['invoices'],
        });
        queryClient.invalidateQueries({
            queryKey: ['invoice', props.invoice.id],
        });
    },
});

async function handleDelete() {
    await handleApiRequestNotifications(
        () => deleteInvoiceMutation.mutateAsync(),
        'Invoice deleted successfully',
        'Failed to delete invoice'
    );
}

function handleEdit() {
    router.visit(`/invoices/${props.invoice.id}`);
}

async function handleSetPaid() {
    const today = getDayJsInstance()().format('YYYY-MM-DD');
    await handleApiRequestNotifications(
        () => updateInvoicePaidStatusMutation.mutateAsync(today),
        'Invoice marked as paid successfully',
        'Failed to mark invoice as paid'
    );
}

async function handleSetUnpaid() {
    await handleApiRequestNotifications(
        () => updateInvoicePaidStatusMutation.mutateAsync(null),
        'Invoice marked as unpaid successfully',
        'Failed to mark invoice as unpaid'
    );
}

const organization = inject<ComputedRef<Organization>>('organization');
</script>

<template>
    <TableRow :href="`/invoices/${invoice.id}`">
        <div
            class="whitespace-nowrap flex items-center space-x-5 3xl:pl-12 py-4 pr-3 text-sm font-medium text-text-primary pl-4 sm:pl-6 lg:pl-8 3xl:pl-12">
            <span>
                {{ invoice.reference }}
            </span>
        </div>
        <div class="px-3 py-4 text-sm text-text-primary">
            {{ invoice.buyer_name }}
        </div>
        <div class="px-3 py-4 text-sm text-text-primary">
            {{ invoice.date ? formatDate(invoice.date, organization?.date_format) : '--' }}
        </div>
        <div class="px-3 py-4 text-sm text-text-primary">
            {{ invoice.due_at ? formatDate(invoice.due_at, organization?.date_format) : '--' }}
        </div>
        <div class="px-3 py-4 text-sm text-text-primary">
            {{
                invoice.paid_date ? formatDate(invoice.paid_date, organization?.date_format) : '--'
            }}
        </div>
        <div class="px-3 py-2 text-sm flex items-center text-text-primary">
            <InvoiceDownloadDropdown :invoice="invoice">
                <template #default="{ loading }">
                    <Button variant="ghost" class="text-text-tertiary hover:text-text-primary">
                        <LoadingSpinner v-if="loading" class="mx-0" />
                        <ArrowDownTrayIcon v-else class="h-5 w-5" />
                    </Button>
                </template>
            </InvoiceDownloadDropdown>
        </div>
        <div
            class="relative whitespace-nowrap flex items-center space-x-4 pl-3 text-right text-sm font-medium sm:pr-0 pr-4 sm:pr-6 lg:pr-8 3xl:pr-12">
            <InvoiceMoreOptionsDropdown
                :invoice="invoice"
                @edit="handleEdit"
                @delete="handleDelete"
                @set-paid="handleSetPaid"
                @set-unpaid="handleSetUnpaid" />
        </div>
    </TableRow>
</template>

<style scoped></style>
